// ============================================================
// THE COMPANY — Zustand store (with immer middleware)
// ============================================================

import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { api, TOKEN_KEY } from "./api";
import type {
  Role,
  RoleStatus,
  Conversation,
  Dependency,
  Decision,
  ActionItem,
} from "../lib/types";

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

interface CompanyState {
  // Data
  roles: Role[];
  conversations: Record<string, Conversation[]>; // keyed by roleId
  dependencies: Dependency[];
  decisions: Decision[];
  actionItems: Record<string, ActionItem[]>; // keyed by roleId

  // UI
  selectedRoleId: string | null;
  sidePanelOpen: boolean;
  boardViewOpen: boolean;
  searchQuery: string;
  isAuthenticated: boolean;

  // Actions — auth
  authenticate: (password: string) => Promise<boolean>;

  // Actions — bulk load
  fetchAll: () => Promise<void>;

  // Actions — roles
  addRole: (data: {
    name: string;
    title: string;
    parentId?: string | null;
    persona?: string;
    responsibilities?: string;
  }) => Promise<Role>;
  updateRole: (
    id: string,
    data: Partial<
      Pick<
        Role,
        | "name"
        | "title"
        | "persona"
        | "responsibilities"
        | "notes"
        | "parentId"
        | "status"
        | "sortOrder"
      >
    >,
  ) => Promise<void>;
  deleteRole: (id: string) => Promise<void>;
  reparentRole: (id: string, newParentId: string | null) => Promise<void>;

  // Actions — conversations
  fetchConversations: (roleId: string) => Promise<void>;
  addConversation: (
    roleId: string,
    data: { asked: string; answered: string },
  ) => Promise<Conversation>;
  deleteConversation: (conversationId: string, roleId: string) => Promise<void>;

  // Actions — dependencies
  addDependency: (data: {
    sourceId: string;
    targetId: string;
    label?: string;
  }) => Promise<Dependency>;
  deleteDependency: (id: string) => Promise<void>;

  // Actions — decisions
  addDecision: (data: { roleId: string; content: string }) => Promise<Decision>;
  deleteDecision: (id: string) => Promise<void>;
  informRoles: (decisionId: string, roleIds: string[]) => Promise<void>;

  // Actions — action items
  fetchActionItems: (roleId: string) => Promise<void>;
  addActionItem: (roleId: string, text: string) => Promise<ActionItem>;
  updateActionItem: (
    id: string,
    roleId: string,
    data: Partial<Pick<ActionItem, "text" | "completed">>,
  ) => Promise<void>;
  deleteActionItem: (id: string, roleId: string) => Promise<void>;

  // Actions — UI
  selectRole: (id: string | null) => void;
  toggleBoardView: () => void;
  setSearchQuery: (query: string) => void;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useCompanyStore = create<CompanyState>()(
  immer((set, get) => ({
    // -- Initial state -------------------------------------------------------
    roles: [],
    conversations: {},
    dependencies: [],
    decisions: [],
    actionItems: {},
    selectedRoleId: null,
    sidePanelOpen: false,
    boardViewOpen: false,
    searchQuery: "",
    isAuthenticated: !!sessionStorage.getItem(TOKEN_KEY),

    // -- Auth ----------------------------------------------------------------
    authenticate: async (password) => {
      try {
        const { token } = await api.post<{ token: string }>("/api/auth", {
          password,
        });
        sessionStorage.setItem(TOKEN_KEY, token);
        set((s) => {
          s.isAuthenticated = true;
        });
        return true;
      } catch {
        return false;
      }
    },

    // -- Bulk load -----------------------------------------------------------
    fetchAll: async () => {
      const [roles, dependencies, decisions, allActionItems] =
        await Promise.all([
          api.get<Role[]>("/api/roles"),
          api.get<Dependency[]>("/api/dependencies"),
          api.get<Decision[]>("/api/decisions"),
          api.get<ActionItem[]>("/api/action-items"),
        ]);
      // Group action items by roleId
      const grouped: Record<string, ActionItem[]> = {};
      for (const item of allActionItems) {
        if (!grouped[item.roleId]) grouped[item.roleId] = [];
        grouped[item.roleId].push(item);
      }
      set((s) => {
        s.roles = roles;
        s.dependencies = dependencies;
        s.decisions = decisions;
        s.actionItems = grouped;
      });
    },

    // -- Roles ---------------------------------------------------------------
    addRole: async (data) => {
      const role = await api.post<Role>("/api/roles", data);
      set((s) => {
        s.roles.push(role);
      });
      return role;
    },

    updateRole: async (id, data) => {
      const updated = await api.put<Role>(`/api/roles/${id}`, data);
      set((s) => {
        const idx = s.roles.findIndex((r) => r.id === id);
        if (idx !== -1) s.roles[idx] = updated;
      });
    },

    deleteRole: async (id) => {
      await api.del(`/api/roles/${id}`);
      set((s) => {
        s.roles = s.roles.filter((r) => r.id !== id);
        delete s.conversations[id];
        delete s.actionItems[id];
        s.dependencies = s.dependencies.filter(
          (d) => d.sourceId !== id && d.targetId !== id,
        );
        s.decisions = s.decisions.filter((d) => d.roleId !== id);
        if (s.selectedRoleId === id) {
          s.selectedRoleId = null;
          s.sidePanelOpen = false;
        }
      });
    },

    reparentRole: async (id, newParentId) => {
      const updated = await api.put<Role>(`/api/roles/${id}`, {
        parentId: newParentId,
      });
      set((s) => {
        const idx = s.roles.findIndex((r) => r.id === id);
        if (idx !== -1) s.roles[idx] = updated;
      });
    },

    // -- Conversations -------------------------------------------------------
    fetchConversations: async (roleId) => {
      const convos = await api.get<Conversation[]>(
        `/api/roles/${roleId}/conversations`,
      );
      set((s) => {
        s.conversations[roleId] = convos;
      });
    },

    addConversation: async (roleId, data) => {
      const convo = await api.post<Conversation>(
        `/api/roles/${roleId}/conversations`,
        data,
      );
      set((s) => {
        if (!s.conversations[roleId]) s.conversations[roleId] = [];
        s.conversations[roleId].push(convo);
      });
      return convo;
    },

    deleteConversation: async (conversationId, roleId) => {
      await api.del(`/api/conversations/${conversationId}`);
      set((s) => {
        if (s.conversations[roleId]) {
          s.conversations[roleId] = s.conversations[roleId].filter(
            (c) => c.id !== conversationId,
          );
        }
      });
    },

    // -- Dependencies --------------------------------------------------------
    addDependency: async (data) => {
      const dep = await api.post<Dependency>("/api/dependencies", data);
      set((s) => {
        s.dependencies.push(dep);
      });
      return dep;
    },

    deleteDependency: async (id) => {
      await api.del(`/api/dependencies/${id}`);
      set((s) => {
        s.dependencies = s.dependencies.filter((d) => d.id !== id);
      });
    },

    // -- Decisions -----------------------------------------------------------
    addDecision: async (data) => {
      const decision = await api.post<Decision>("/api/decisions", data);
      set((s) => {
        s.decisions.push(decision);
      });
      return decision;
    },

    deleteDecision: async (id) => {
      await api.del(`/api/decisions/${id}`);
      set((s) => {
        s.decisions = s.decisions.filter((d) => d.id !== id);
      });
    },

    informRoles: async (decisionId, roleIds) => {
      await api.post(`/api/decisions/${decisionId}/inform`, { roleIds });
      set((s) => {
        const decision = s.decisions.find((d) => d.id === decisionId);
        if (decision) {
          const existing = new Set(decision.informedRoleIds);
          for (const rid of roleIds) existing.add(rid);
          decision.informedRoleIds = [...existing];
        }
      });
    },

    // -- Action Items --------------------------------------------------------
    fetchActionItems: async (roleId) => {
      const items = await api.get<ActionItem[]>(
        `/api/roles/${roleId}/action-items`,
      );
      set((s) => {
        s.actionItems[roleId] = items;
      });
    },

    addActionItem: async (roleId, text) => {
      const item = await api.post<ActionItem>(
        `/api/roles/${roleId}/action-items`,
        { text },
      );
      set((s) => {
        if (!s.actionItems[roleId]) s.actionItems[roleId] = [];
        s.actionItems[roleId].push(item);
      });
      return item;
    },

    updateActionItem: async (id, roleId, data) => {
      const updated = await api.put<ActionItem>(
        `/api/action-items/${id}`,
        data,
      );
      set((s) => {
        if (s.actionItems[roleId]) {
          const idx = s.actionItems[roleId].findIndex((i) => i.id === id);
          if (idx !== -1) s.actionItems[roleId][idx] = updated;
        }
      });
    },

    deleteActionItem: async (id, roleId) => {
      await api.del(`/api/action-items/${id}`);
      set((s) => {
        if (s.actionItems[roleId]) {
          s.actionItems[roleId] = s.actionItems[roleId].filter(
            (i) => i.id !== id,
          );
        }
      });
    },

    // -- UI ------------------------------------------------------------------
    selectRole: (id) => {
      set((s) => {
        s.selectedRoleId = id;
        s.sidePanelOpen = id !== null;
      });
    },

    toggleBoardView: () => {
      set((s) => {
        s.boardViewOpen = !s.boardViewOpen;
      });
    },

    setSearchQuery: (query) => {
      set((s) => {
        s.searchQuery = query;
      });
    },
  })),
);
